import { useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { BookLock, X } from 'lucide-react';
import { ReserviertInhalt } from './ReserviertInhalt';
import { faengtAussenklickAb } from '@/onboarding/tourSchutz';
import { useZeigerFein } from '@/hooks/useLayout';

// ---------------------------------------------------------------------
// Das Reserviert-Blatt (02.11.2026, Skizze „Reserviert-Blatt über dem
// Dashboard", vom Betreiber freigegeben).
//
// DER BEFUND: „Das häufigste Problem ist mit ‚reserviert'." Wer am Tresen
// fragen will, ob ein Exemplar vergeben ist, musste die Seite verlassen,
// auf der er gerade arbeitet — und fand danach seine Suche nicht wieder.
//
// DIE LÖSUNG: Die Frage legt sich ÜBER jede Seite, die Seite darunter
// bleibt stehen. Geöffnet über den Knopf oben rechts oder am PC mit der
// Taste R. Der Begriff steht in der Adresse (`?reserviert=…`): Ein Tipp
// auf einen Treffer öffnet die Bestellung, und „Zurück" bringt das Blatt
// mit derselben Antwort wieder (E49).
// ---------------------------------------------------------------------

export type ReserviertBlattSuche = { reserviert?: string };

/** Öffnet das Blatt, optional schon mit einer Frage (Titel-Blatt). */
export function useReserviertOeffnen(): (begriff?: string) => void {
  const navigate = useNavigate();
  return (begriff = '') => {
    void navigate({
      // Ein eigener Verlaufsschritt: Die Zurück-Taste schließt das Blatt.
      search: ((prev: Record<string, unknown>) => ({ ...prev, reserviert: begriff })) as never
    } as never);
  };
}

/** Ein Tastendruck, der kein Tippen ist? (Feld, Textbereich, editierbar) */
function tipptGerade(ziel: EventTarget | null): boolean {
  if (!(ziel instanceof HTMLElement)) return false;
  const tag = ziel.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || ziel.isContentEditable;
}

export function ReserviertBlatt() {
  const navigate = useNavigate();
  const pfad = useLocation({ select: (l) => l.pathname });
  const { reserviert } = useSearch({ strict: false }) as ReserviertBlattSuche;
  const offen = reserviert !== undefined;
  const oeffnen = useReserviertOeffnen();

  // Taste R am PC — nur, wenn niemand gerade tippt, kein Dialog offen ist
  // und keine Steuertaste mitgedrückt wird (Strg+R lädt neu).
  useEffect(() => {
    function taste(e: KeyboardEvent): void {
      if (e.key !== 'r' && e.key !== 'R') return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.repeat) return;
      if (tipptGerade(e.target)) return;
      if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return;
      if (pfad === '/reserviert') return;
      e.preventDefault();
      oeffnen();
    }
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, [oeffnen, pfad]);

  function schliessen(): void {
    void navigate({
      search: ((prev: Record<string, unknown>) => ({ ...prev, reserviert: undefined })) as never,
      replace: true
    } as never);
  }

  function fragen(begriff: string): void {
    void navigate({
      search: ((prev: Record<string, unknown>) => ({ ...prev, reserviert: begriff })) as never,
      replace: true
    } as never);
  }

  return (
    <Dialog.Root open={offen} onOpenChange={(o) => !o && schliessen()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/30" />
        <Dialog.Content
          onPointerDownOutside={faengtAussenklickAb}
          onInteractOutside={faengtAussenklickAb}
          aria-describedby={undefined}
          data-testid="reserviert-blatt"
          className="dialog-mobil-unten fixed right-4 top-16 z-50 max-h-[calc(100dvh-5rem)] w-[min(36rem,calc(100vw-2rem))] overflow-y-auto rounded-lg border border-line bg-cream p-5 shadow-lg"
        >
          <div className="mb-3 flex items-start justify-between gap-3">
            <Dialog.Title className="flex items-center gap-2 font-serif text-lg text-ink">
              <BookLock aria-hidden className="h-5 w-5 text-akzent" />
              Reserviert?
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Blatt schließen"
                className="inline-flex h-11 w-11 items-center justify-center rounded text-ink-soft hover:bg-cream-dark"
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>
          {offen && <ReserviertInhalt begriff={reserviert ?? ''} onFragen={fragen} />}
          <p className="mt-4 text-xs text-ink-faint">
            Ein Tipp auf einen Treffer öffnet die Bestellung. „Zurück" bringt dieses Blatt mit
            derselben Antwort wieder.
          </p>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Der Knopf oben rechts. Auf der Reserviert-Seite selbst entfällt er. */
export function ReserviertKnopf({
  kompakt = false,
  kompaktUnterXl = false
}: {
  kompakt?: boolean;
  /** Obere Leiste am PC: unter 1280 px nur das Symbol (Skizze D). */
  kompaktUnterXl?: boolean;
}) {
  const pfad = useLocation({ select: (l) => l.pathname });
  const oeffnen = useReserviertOeffnen();
  const zeigerFein = useZeigerFein();
  if (pfad === '/reserviert') return null;
  return (
    <button
      type="button"
      onClick={() => oeffnen()}
      title="Reserviert? — am PC auch mit der Taste R"
      aria-keyshortcuts="R"
      className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center gap-1.5 rounded-full border border-akzent px-3 text-sm text-akzent hover:bg-akzent-sanft"
    >
      <BookLock aria-hidden className="h-4 w-4" />
      {kompakt ? (
        <span className="sr-only">Reserviert?</span>
      ) : kompaktUnterXl ? (
        <span className="sr-only xl:not-sr-only">Reserviert?</span>
      ) : (
        'Reserviert?'
      )}
      {!kompakt && zeigerFein && (
        <kbd
          className={`rounded border border-line px-1 text-[10px] text-ink-faint ${kompaktUnterXl ? 'hidden xl:inline' : ''}`}
        >
          R
        </kbd>
      )}
    </button>
  );
}
